import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, Brain, Loader2, AlertCircle } from "lucide-react";
import type { TelegramUser } from "../../types";
import { useKnowledgeAssessment, type GeneratingProgress } from "../../hooks/useKnowledgeAssessment";
import { KnowledgeQuiz } from "./KnowledgeQuiz";
import { KnowledgeRoadmap } from "./KnowledgeRoadmap";
import type { TopicSelectPayload } from "./TopicPanel";

interface AITutorAssessmentPanelProps {
  user: TelegramUser | null;
  isMobile?: boolean;
  recommendedCourseSlug?: string | null;
  onTopicSelect?: (payload: TopicSelectPayload | null) => void;
  /** Скрыть шапку (когда заголовок и закрытие рендерит AITutorDualModePanel) */
  omitHeader?: boolean;
}

// ─── Idle / unauthenticated screen ───────────────────────────────────────────

function IdleScreen({ onStart }: { onStart: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-8 text-center">
      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
        <Brain className="w-7 h-7 text-primary" />
      </div>
      <h3 className="text-lg font-semibold text-neutral-text mb-2">
        Персональный Roadmap
      </h3>
      <p className="text-sm text-neutral-text/60 leading-relaxed mb-6 max-w-xs">
        AI Tutor оценит твои знания ClickHouse и составит карту изучения с&nbsp;рекомендацией подходящего курса
      </p>

      <div className="w-full max-w-xs space-y-2 text-left mb-6">
        {[
          "5 вопросов об опыте и задачах",
          "Knowledge Graph текущих знаний",
          "Персональный Roadmap с приоритетами",
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2.5 text-sm text-neutral-text/70">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
              {i + 1}
            </span>
            {item}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onStart}
        className="w-full max-w-xs py-3 px-6 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary/90 transition-colors shadow-sm"
      >
        Оценить мои знания
      </button>
    </div>
  );
}

function UnauthScreen() {
  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-8 text-center">
      <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-5">
        <Brain className="w-7 h-7 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-neutral-text mb-2">
        AI Tutor Assessment
      </h3>
      <p className="text-sm text-neutral-text/60 leading-relaxed mb-6 max-w-xs">
        Войди через Telegram, чтобы пройти оценку знаний и получить персональный roadmap
      </p>
      <a
        href="/login"
        className="w-full max-w-xs py-3 px-6 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary/90 transition-colors text-center block"
      >
        Войти через Telegram
      </a>
    </div>
  );
}

// ─── Live generating screen ───────────────────────────────────────────────────


function GeneratingScreen({ progress }: { progress: GeneratingProgress | null }) {
  const tokenStream = progress?.tokenStream ?? "";

  const [displayText, setDisplayText] = useState("Анализирую ответы...");
  const [visible, setVisible] = useState(true);
  const prevCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const streamedLabels = useMemo(() => {
    if (!tokenStream) return [];
    const regex = /"label":\s*"([^"\\]+)"/g;
    const labels: string[] = [];
    let match;
    while ((match = regex.exec(tokenStream)) !== null) labels.push(match[1]);
    return [...new Set(labels)];
  }, [tokenStream]);

  const fadeTo = useCallback((text: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
    timerRef.current = setTimeout(() => {
      setDisplayText(text);
      setVisible(true);
    }, 250);
  }, []);

  // Trigger on each new unique label arriving
  useEffect(() => {
    const count = streamedLabels.length;
    if (count === 0 || count <= prevCountRef.current) return;
    prevCountRef.current = count;

    // Inject hints at specific label counts instead of showing a topic label
    if (count === 6) { fadeTo("Ещё чуть-чуть..."); return; }
    if (count === 14) { fadeTo("Ещё совсем чуть-чуть..."); return; }

    fadeTo(streamedLabels[count - 1]);
  }, [streamedLabels, fadeTo]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Status bar */}
      <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 border-b border-neutral-border/40 bg-gray-50/60">
        <Loader2 className="w-3.5 h-3.5 text-primary animate-spin flex-shrink-0" />
        <span className="text-xs text-neutral-text/60 truncate">
          {progress?.status ?? "Подготовка..."}
        </span>
      </div>

      {/* Cycling label — one at a time, fades in/out */}
      <div className="flex-1 flex flex-col items-center justify-center gap-3 px-8 text-center">
        <div className="flex gap-1 mb-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
        <p
          className="text-sm text-neutral-text/70 font-medium"
          style={{ opacity: visible ? 1 : 0, transition: "opacity 0.25s ease" }}
        >
          {displayText}
        </p>
      </div>
    </div>
  );
}

function ErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-8 text-center gap-4">
      <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
        <AlertCircle className="w-7 h-7 text-red-400" />
      </div>
      <div>
        <h3 className="text-base font-semibold text-neutral-text mb-1">Что-то пошло не так</h3>
        <p className="text-sm text-neutral-text/60 max-w-xs">{message}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        Попробовать снова
      </button>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-6 h-6 text-primary/40 animate-spin" />
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export function AITutorAssessmentPanel({
  user,
  isMobile = false,
  onTopicSelect,
  omitHeader = false,
}: AITutorAssessmentPanelProps) {
  const {
    state,
    assessment,
    progress,
    error,
    startQuiz,
    submitQuiz,
    resetAssessment,
    fetchAdaptiveConcepts,
  } = useKnowledgeAssessment(user?.id ?? null);

  const handleQuizSubmit = async (answers: Record<string, unknown>) => {
    if (!user?.id) return;
    await submitQuiz(answers, user.id);
  };

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {!omitHeader && (
        <div className="flex-shrink-0 flex items-center justify-between border-b border-neutral-border px-4 py-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <h2 className="text-sm font-semibold text-neutral-text">AI Tutor</h2>
              {state === "roadmap" && assessment && (
                <span className="text-xs text-neutral-text/70">
                  Roadmap готов — AI Tutor учитывает ваш текущий уровень в ответах
                </span>
              )}
              {state === "generating" && progress && progress.areas.length > 0 && (
                <span className="text-xs text-neutral-text/70">{progress.areas.length} областей...</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 min-h-0">
        {!user && <UnauthScreen />}
        {user && state === "loading_existing" && <LoadingScreen />}
        {user && state === "idle" && <IdleScreen onStart={startQuiz} />}
        {user && state === "quiz" && (
          <KnowledgeQuiz onSubmit={handleQuizSubmit} isSubmitting={false} onFetchConcepts={fetchAdaptiveConcepts} />
        )}
        {user && state === "generating" && <GeneratingScreen progress={progress} />}
        {user && state === "error" && (
          <ErrorScreen
            message={error ?? "Неизвестная ошибка"}
            onRetry={resetAssessment}
          />
        )}
        {user && state === "roadmap" && assessment && (
          <KnowledgeRoadmap
            assessment={assessment}
            onRetake={resetAssessment}
            isMobile={isMobile}
            onTopicSelect={onTopicSelect}
          />
        )}
      </div>
    </div>
  );
}
